import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { api, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  ArrowLeft, Plus, Save, Trash2, Eye, EyeOff, Edit,
  Bold, Italic, Heading1, Heading2, Heading3, Link as LinkIcon,
  List, ListOrdered, Quote, Code, Image as ImageIcon, Upload,
} from "lucide-react";

const EMPTY = {
  title: "", slug: "", meta_title: "", meta_description: "",
  excerpt: "", content: "", cover_image: "", tags: [], status: "draft",
};

export default function AdminBlogPage() {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(null); // null | 'new' | postId
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [tagInput, setTagInput] = useState("");

  const isAdmin = user?.is_admin;

  const load = async () => {
    try {
      const { data } = await api.get("/admin/blog");
      setPosts(data.posts || []);
    } catch (e) {
      toast.error("Erreur de chargement");
    }
  };

  useEffect(() => { if (isAdmin) load(); }, [isAdmin]);

  if (!user) return <div className="p-10 text-white/60">Chargement…</div>;
  if (!isAdmin) return (
    <div className="p-10 max-w-2xl">
      <h1 className="font-display text-3xl font-bold mb-2">Accès restreint</h1>
      <p className="text-white/60">Cette section est réservée à l'administratrice.</p>
    </div>
  );

  const openNew = () => { setEditing("new"); setForm(EMPTY); setTagInput(""); };
  const openEdit = async (post_id) => {
    const { data } = await api.get(`/admin/blog/${post_id}`);
    setEditing(post_id);
    setForm({ ...EMPTY, ...data });
    setTagInput("");
  };

  const save = async () => {
    if (!form.title || !form.excerpt || !form.meta_description || !form.content) {
      toast.error("Titre, extrait, meta description et contenu sont requis");
      return;
    }
    setSaving(true);
    try {
      if (editing === "new") {
        await api.post("/admin/blog", form);
        toast.success("Article créé");
      } else {
        await api.put(`/admin/blog/${editing}`, form);
        toast.success("Article mis à jour");
      }
      setEditing(null);
      await load();
    } catch (e) {
      toast.error("Erreur de sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  const del = async (post_id) => {
    if (!window.confirm("Supprimer cet article définitivement ?")) return;
    await api.delete(`/admin/blog/${post_id}`);
    toast.success("Article supprimé");
    await load();
  };

  const addTag = () => {
    const t = tagInput.trim();
    if (t && !form.tags.includes(t)) setForm({ ...form, tags: [...form.tags, t] });
    setTagInput("");
  };
  const removeTag = (t) => setForm({ ...form, tags: form.tags.filter((x) => x !== t) });

  return (
    <div className="p-6 lg:p-10 max-w-[1400px]">
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-[#FF8C42] mb-2">Admin · Blog</div>
          <h1 className="font-display text-4xl md:text-5xl font-black tracking-tight" data-testid="admin-blog-title">
            Articles du blog
          </h1>
        </div>
        {!editing && (
          <button onClick={openNew} data-testid="admin-blog-new-btn"
            className="inline-flex items-center gap-2 bg-[#FF8C42] text-[#0A1225] px-5 py-2.5 rounded-full font-mono text-xs uppercase tracking-widest hover:bg-[#FAF0DE] transition-colors">
            <Plus className="w-4 h-4" /> Nouvel article
          </button>
        )}
        {editing && (
          <button onClick={() => setEditing(null)} className="text-xs font-mono uppercase tracking-widest text-white/60 hover:text-white flex items-center gap-2">
            <ArrowLeft className="w-3 h-3" /> Retour à la liste
          </button>
        )}
      </div>

      {editing ? (
        <EditorForm
          form={form} setForm={setForm}
          tagInput={tagInput} setTagInput={setTagInput}
          addTag={addTag} removeTag={removeTag}
          save={save} saving={saving}
        />
      ) : (
        <div className="rounded-3xl border border-white/10 bg-[#132038]/60 backdrop-blur overflow-hidden">
          {posts.length === 0 ? (
            <div className="p-12 text-center text-white/50">
              <div className="font-display text-2xl font-bold mb-2 text-white">Aucun article pour le moment</div>
              <p className="mb-4">Cliquez sur "Nouvel article" pour créer votre premier post.</p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-white/5 text-[10px] font-mono uppercase tracking-widest text-white/50">
                <tr>
                  <th className="text-left px-5 py-3">Titre</th>
                  <th className="text-left px-5 py-3">Slug</th>
                  <th className="text-left px-5 py-3">Statut</th>
                  <th className="text-left px-5 py-3">Modifié</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {posts.map((p) => (
                  <tr key={p.post_id} className="border-t border-white/5" data-testid={`admin-row-${p.post_id}`}>
                    <td className="px-5 py-3 font-medium">{p.title}</td>
                    <td className="px-5 py-3 font-mono text-xs text-white/60">/blog/{p.slug}</td>
                    <td className="px-5 py-3">
                      <span className={`chip ${p.status === "published" ? "text-[#00E5A0] border-[#00E5A0]/40" : "text-white/60 border-white/20"}`}>
                        {p.status === "published" ? <><Eye className="w-3 h-3" /> Publié</> : <><EyeOff className="w-3 h-3" /> Brouillon</>}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-xs text-white/50 font-mono">{(p.updated_at || "").slice(0, 10)}</td>
                    <td className="px-5 py-3 text-right">
                      <div className="flex items-center gap-2 justify-end">
                        <Link to={`/blog/${p.slug}`} target="_blank" className="p-2 rounded-lg hover:bg-white/10 text-white/60 hover:text-white" title="Voir">
                          <Eye className="w-4 h-4" />
                        </Link>
                        <button onClick={() => openEdit(p.post_id)} className="p-2 rounded-lg hover:bg-white/10 text-white/60 hover:text-white" title="Éditer">
                          <Edit className="w-4 h-4" />
                        </button>
                        <button onClick={() => del(p.post_id)} className="p-2 rounded-lg hover:bg-[#FF5B4F]/20 text-white/60 hover:text-[#FF5B4F]" title="Supprimer">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

/* --------------------------- Editor Form ------------------------------ */

function EditorForm({ form, setForm, tagInput, setTagInput, addTag, removeTag, save, saving }) {
  const textareaRef = useRef(null);
  const inlineFileRef = useRef(null);
  const coverFileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);

  /** Insert text around/at the current caret in the textarea. */
  const wrapSelection = (before, after = "", placeholder = "") => {
    const ta = textareaRef.current;
    if (!ta) return;
    const { selectionStart: s, selectionEnd: e, value } = ta;
    const sel = value.slice(s, e) || placeholder;
    const next = value.slice(0, s) + before + sel + after + value.slice(e);
    setForm((f) => ({ ...f, content: next }));
    // restore caret after paint
    requestAnimationFrame(() => {
      ta.focus();
      const pos = s + before.length + sel.length;
      ta.setSelectionRange(pos, pos);
    });
  };

  const insertLinePrefix = (prefix) => {
    const ta = textareaRef.current;
    if (!ta) return;
    const { selectionStart: s, value } = ta;
    const lineStart = value.lastIndexOf("\n", s - 1) + 1;
    const next = value.slice(0, lineStart) + prefix + value.slice(lineStart);
    setForm((f) => ({ ...f, content: next }));
    requestAnimationFrame(() => {
      ta.focus();
      const pos = s + prefix.length;
      ta.setSelectionRange(pos, pos);
    });
  };

  const uploadImage = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/uploads/image", fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return data.url;
  };

  const onInlineImage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadImage(file);
      wrapSelection(`![${file.name.replace(/\.[^.]+$/, "")}](${url})`, "", "");
      toast.success("Image insérée");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec de l'upload");
    } finally {
      setUploading(false);
    }
  };

  const onCoverImage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploadingCover(true);
    try {
      const url = await uploadImage(file);
      setForm((f) => ({ ...f, cover_image: url }));
      toast.success("Cover mise à jour");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec de l'upload");
    } finally {
      setUploadingCover(false);
    }
  };

  return (
    <div className="rounded-3xl border border-white/10 bg-[#132038]/60 backdrop-blur p-6 md:p-8 space-y-5">
      <Field label="Titre*" testid="admin-title">
        <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
          className="w-full bg-[#0A1225]/60 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-[#FF8C42]" />
      </Field>

      <Field label="Slug (URL) — laisser vide pour générer automatiquement" testid="admin-slug">
        <input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} placeholder="mon-article-seo-2026"
          className="w-full bg-[#0A1225]/60 border border-white/10 rounded-xl px-4 py-3 text-white font-mono text-sm focus:outline-none focus:border-[#FF8C42]" />
      </Field>

      <div className="grid md:grid-cols-2 gap-5">
        <Field label="Meta title (SEO — 60 car. max)" testid="admin-metatitle">
          <input maxLength={70} value={form.meta_title} onChange={(e) => setForm({ ...form, meta_title: e.target.value })}
            className="w-full bg-[#0A1225]/60 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-[#FF8C42]" />
        </Field>

        <Field label="Image de couverture" testid="admin-cover">
          <div className="flex gap-2 items-start">
            <input value={form.cover_image} onChange={(e) => setForm({ ...form, cover_image: e.target.value })}
              placeholder="https://… ou upload →"
              className="flex-1 bg-[#0A1225]/60 border border-white/10 rounded-xl px-4 py-3 text-white font-mono text-xs focus:outline-none focus:border-[#FF8C42]" />
            <input ref={coverFileRef} type="file" accept="image/*" onChange={onCoverImage} className="hidden" data-testid="admin-cover-file" />
            <button type="button" onClick={() => coverFileRef.current?.click()} disabled={uploadingCover}
              data-testid="admin-cover-upload-btn"
              className="shrink-0 inline-flex items-center gap-2 px-4 py-3 rounded-xl bg-[#FF8C42]/15 text-[#FF8C42] border border-[#FF8C42]/40 text-xs font-mono uppercase tracking-widest hover:bg-[#FF8C42]/25 disabled:opacity-50">
              <Upload className="w-3.5 h-3.5" /> {uploadingCover ? "…" : "Upload"}
            </button>
          </div>
          {form.cover_image && (
            <div className="mt-2 rounded-xl overflow-hidden border border-white/10 max-w-xs">
              <img src={form.cover_image} alt="cover" className="w-full h-auto" />
            </div>
          )}
        </Field>
      </div>

      <Field label="Meta description* (SEO — 150-160 car.)" testid="admin-metadesc">
        <textarea maxLength={170} rows={2} value={form.meta_description} onChange={(e) => setForm({ ...form, meta_description: e.target.value })}
          className="w-full bg-[#0A1225]/60 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-[#FF8C42]" />
        <div className="text-[10px] font-mono text-white/40 mt-1">{form.meta_description.length}/170</div>
      </Field>

      <Field label="Extrait* (affiché sur la liste)" testid="admin-excerpt">
        <textarea rows={2} value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })}
          className="w-full bg-[#0A1225]/60 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-[#FF8C42]" />
      </Field>

      {/* Markdown Editor with Live Preview */}
      <div data-testid="admin-content">
        <label className="block text-[10px] font-mono uppercase tracking-widest text-white/60 mb-2">
          Contenu* — Markdown avec aperçu live
        </label>

        {/* Toolbar */}
        <div className="flex flex-wrap gap-1 mb-2 p-2 rounded-t-xl bg-[#0A1225]/80 border border-white/10 border-b-0">
          <ToolbarBtn onClick={() => wrapSelection("**", "**", "gras")} title="Gras (Ctrl+B)" testid="tb-bold"><Bold className="w-3.5 h-3.5" /></ToolbarBtn>
          <ToolbarBtn onClick={() => wrapSelection("*", "*", "italique")} title="Italique" testid="tb-italic"><Italic className="w-3.5 h-3.5" /></ToolbarBtn>
          <span className="w-px bg-white/10 mx-1" />
          <ToolbarBtn onClick={() => insertLinePrefix("# ")} title="Titre 1" testid="tb-h1"><Heading1 className="w-3.5 h-3.5" /></ToolbarBtn>
          <ToolbarBtn onClick={() => insertLinePrefix("## ")} title="Titre 2" testid="tb-h2"><Heading2 className="w-3.5 h-3.5" /></ToolbarBtn>
          <ToolbarBtn onClick={() => insertLinePrefix("### ")} title="Titre 3" testid="tb-h3"><Heading3 className="w-3.5 h-3.5" /></ToolbarBtn>
          <ToolbarBtn onClick={() => insertLinePrefix("> ")} title="Citation" testid="tb-quote"><Quote className="w-3.5 h-3.5" /></ToolbarBtn>
          <span className="w-px bg-white/10 mx-1" />
          <ToolbarBtn onClick={() => insertLinePrefix("- ")} title="Liste" testid="tb-ul"><List className="w-3.5 h-3.5" /></ToolbarBtn>
          <ToolbarBtn onClick={() => insertLinePrefix("1. ")} title="Liste numérotée" testid="tb-ol"><ListOrdered className="w-3.5 h-3.5" /></ToolbarBtn>
          <ToolbarBtn onClick={() => wrapSelection("`", "`", "code")} title="Code inline" testid="tb-code"><Code className="w-3.5 h-3.5" /></ToolbarBtn>
          <span className="w-px bg-white/10 mx-1" />
          <ToolbarBtn onClick={() => {
            const url = window.prompt("URL du lien :");
            if (url) wrapSelection("[", `](${url})`, "texte du lien");
          }} title="Lien" testid="tb-link"><LinkIcon className="w-3.5 h-3.5" /></ToolbarBtn>
          <ToolbarBtn onClick={() => inlineFileRef.current?.click()} title="Insérer une image" testid="tb-image" disabled={uploading}>
            {uploading ? <span className="text-[10px] px-1">…</span> : <ImageIcon className="w-3.5 h-3.5" />}
          </ToolbarBtn>
          <input ref={inlineFileRef} type="file" accept="image/*" onChange={onInlineImage} className="hidden" data-testid="admin-inline-file" />
        </div>

        {/* Split panes */}
        <div className="grid md:grid-cols-2 gap-0 border border-white/10 rounded-b-xl overflow-hidden">
          <textarea
            ref={textareaRef}
            value={form.content}
            onChange={(e) => setForm({ ...form, content: e.target.value })}
            onKeyDown={(e) => {
              if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "b") {
                e.preventDefault(); wrapSelection("**", "**", "gras");
              }
              if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "i") {
                e.preventDefault(); wrapSelection("*", "*", "italique");
              }
            }}
            rows={22}
            spellCheck
            data-testid="admin-content-textarea"
            className="w-full bg-[#0A1225]/70 px-4 py-4 text-white font-mono text-sm leading-relaxed focus:outline-none resize-y md:border-r md:border-white/10"
            placeholder="# Titre de l'article&#10;&#10;Écris ton contenu en **markdown**…"
          />
          <div
            data-testid="admin-content-preview"
            className="bg-[#0A1225]/40 px-5 py-4 overflow-y-auto max-h-[600px] prose prose-invert prose-sm max-w-none prose-headings:font-display prose-headings:text-white prose-a:text-[#FF8C42] prose-strong:text-white prose-img:rounded-xl prose-img:border prose-img:border-white/10"
          >
            {form.content ? (
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{form.content}</ReactMarkdown>
            ) : (
              <p className="text-white/30 italic text-sm">L'aperçu s'affichera ici…</p>
            )}
          </div>
        </div>
        <div className="text-[10px] font-mono text-white/40 mt-2">
          Astuces : Ctrl/Cmd+B (gras), Ctrl/Cmd+I (italique). Clic sur l'icône image pour uploader.
        </div>
      </div>

      <Field label="Tags" testid="admin-tags">
        <div className="flex gap-2 flex-wrap mb-2">
          {form.tags.map((t) => (
            <span key={t} className="chip bg-[#FF8C42]/15 text-[#FF8C42] border-[#FF8C42]/40">
              #{t} <button onClick={() => removeTag(t)} className="ml-1 opacity-60 hover:opacity-100">×</button>
            </span>
          ))}
        </div>
        <div className="flex gap-2">
          <input value={tagInput} onChange={(e) => setTagInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addTag())}
            placeholder="Ajouter un tag et Entrée" className="flex-1 bg-[#0A1225]/60 border border-white/10 rounded-xl px-4 py-2 text-white text-sm focus:outline-none focus:border-[#FF8C42]" />
          <button onClick={addTag} className="px-4 py-2 rounded-xl bg-white/10 text-xs font-mono uppercase tracking-widest hover:bg-white/20">Ajouter</button>
        </div>
      </Field>

      <Field label="Statut" testid="admin-status">
        <div className="flex gap-2">
          {["draft", "published"].map((s) => (
            <button key={s} onClick={() => setForm({ ...form, status: s })}
              className={`px-4 py-2 rounded-full text-xs font-mono uppercase tracking-widest border ${form.status === s ? "bg-[#FF8C42] text-[#0A1225] border-[#FF8C42]" : "border-white/10 text-white/60 hover:border-white/30"}`}>
              {s === "draft" ? "Brouillon" : "Publié"}
            </button>
          ))}
        </div>
      </Field>

      <div className="flex gap-3 pt-4">
        <button onClick={save} disabled={saving} data-testid="admin-save-btn"
          className="inline-flex items-center gap-2 bg-[#FF8C42] text-[#0A1225] px-6 py-3 rounded-full font-mono text-xs uppercase tracking-widest hover:bg-[#FAF0DE] transition-colors disabled:opacity-50">
          <Save className="w-4 h-4" /> {saving ? "Sauvegarde…" : "Enregistrer"}
        </button>
      </div>
    </div>
  );
}

function ToolbarBtn({ onClick, title, children, testid, disabled }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      disabled={disabled}
      data-testid={testid}
      className="p-2 rounded-md text-white/70 hover:text-white hover:bg-white/10 transition-colors disabled:opacity-40"
    >
      {children}
    </button>
  );
}

function Field({ label, testid, children }) {
  return (
    <div data-testid={testid}>
      <label className="block text-[10px] font-mono uppercase tracking-widest text-white/60 mb-2">{label}</label>
      {children}
    </div>
  );
}
